import { Download, Copy } from 'lucide-react';

interface OAExtractDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function OAExtractDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: OAExtractDisplayProps) {
  // Parse result to extract 'output' if present
  const parseResult = (data: any) => {
    if (!data) return null;

    // If it's an object with 'output' property, extract it
    if (typeof data === 'object' && data.output) {
      return data.output;
    }

    return data;
  };

  const parsedResult = parseResult(result);

  // Check if this result contains office action extract data
  if (!parsedResult || typeof parsedResult !== 'object') return null;

  // Check for key office action extract properties (matching patentloop-frontend)
  const isOAExtract = parsedResult.action_type !== undefined ||
                      parsedResult.mailing_date !== undefined ||
                      parsedResult.examiner_info !== undefined ||
                      parsedResult.application_info !== undefined ||
                      parsedResult.rejections_summary !== undefined ||
                      parsedResult.prior_art_cited !== undefined;

  if (!isOAExtract) return null;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy to clipboard');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Not specified';
    try {
      // Parse date string as local date to avoid timezone issues
      // Date strings like '2016-01-14' are interpreted as UTC midnight,
      // which can show as previous day in timezones behind UTC
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
        const day = parseInt(parts[2], 10);
        return new Date(year, month, day).toLocaleDateString();
      }
      // Fallback to original parsing
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">Office Action Information</h3>
        </div>
      )}

      <div className="space-y-4">
        {/* Action Type and Dates */}
        {parsedResult.action_type && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2 text-blue-900">{parsedResult.action_type}</h4>
            <div className="text-sm space-y-1">
              {parsedResult.mailing_date && (
                <div><span className="font-medium">Mailed:</span> {formatDate(parsedResult.mailing_date)}</div>
              )}
              {parsedResult.response_deadline && (
                <div className="text-red-700"><span className="font-medium">Response Deadline:</span> {formatDate(parsedResult.response_deadline)}</div>
              )}
              {parsedResult.final_deadline && (
                <div className="text-red-900 font-semibold"><span className="font-medium">Final Deadline:</span> {formatDate(parsedResult.final_deadline)}</div>
              )}
            </div>
          </div>
        )}

        {/* Application Info */}
        {parsedResult.application_info && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Application Information</h4>
            <div className="space-y-1 text-sm">
              {parsedResult.application_info.application_number && (
                <div><span className="font-medium">Application Number:</span> {parsedResult.application_info.application_number}</div>
              )}
              {parsedResult.application_info.filing_date && (
                <div><span className="font-medium">Filing Date:</span> {formatDate(parsedResult.application_info.filing_date)}</div>
              )}
              {parsedResult.application_info.title && (
                <div><span className="font-medium">Title:</span> {parsedResult.application_info.title}</div>
              )}
              {parsedResult.application_info.attorney_docket && (
                <div><span className="font-medium">Attorney Docket:</span> {parsedResult.application_info.attorney_docket}</div>
              )}
            </div>
          </div>
        )}

        {/* Examiner Info */}
        {parsedResult.examiner_info && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Examiner Information</h4>
            <div className="space-y-1 text-sm">
              {parsedResult.examiner_info.name && (
                <div><span className="font-medium">Name:</span> {parsedResult.examiner_info.name}</div>
              )}
              {parsedResult.examiner_info.art_unit && (
                <div><span className="font-medium">Art Unit:</span> {parsedResult.examiner_info.art_unit}</div>
              )}
              {parsedResult.examiner_info.telephone && (
                <div><span className="font-medium">Phone:</span> {parsedResult.examiner_info.telephone}</div>
              )}
              {parsedResult.examiner_info.email && (
                <div><span className="font-medium">Email:</span> {parsedResult.examiner_info.email}</div>
              )}
            </div>
          </div>
        )}

        {/* Claims Status */}
        {(parsedResult.claims_rejected?.length > 0 || parsedResult.allowable_claims?.length > 0) && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3">Claims Status</h4>
            <div className="grid grid-cols-2 gap-4">
              {parsedResult.claims_rejected?.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                  <div className="font-medium mb-2 text-red-900">Rejected Claims</div>
                  <div className="text-sm text-red-800">
                    Claims: {parsedResult.claims_rejected.join(', ')}
                  </div>
                </div>
              )}
              {parsedResult.allowable_claims?.length > 0 && (
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                  <div className="font-medium mb-2 text-green-900">Allowable Claims</div>
                  <div className="text-sm text-green-800">
                    Claims: {parsedResult.allowable_claims.join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rejections Summary */}
        {parsedResult.rejections_summary && Array.isArray(parsedResult.rejections_summary) && parsedResult.rejections_summary.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3 text-yellow-900">Rejections Summary</h4>
            <div className="space-y-3">
              {parsedResult.rejections_summary.map((rejection: any, idx: number) => (
                <div key={idx} className="bg-white p-3 rounded border border-yellow-300">
                  <div className="font-medium mb-1 text-yellow-900">
                    {rejection.rejection_type}
                    {rejection.claims_affected && (
                      <span className="ml-2 text-sm font-normal">(Claims {rejection.claims_affected})</span>
                    )}
                  </div>
                  {rejection.basis && (
                    <div className="text-sm mb-1"><span className="font-medium">Basis:</span> {rejection.basis}</div>
                  )}
                  {rejection.summary && (
                    <div className="text-sm">{rejection.summary}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prior Art References */}
        {parsedResult.prior_art_cited && Array.isArray(parsedResult.prior_art_cited) && parsedResult.prior_art_cited.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-3 text-blue-900">Prior Art References</h4>
            <div className="space-y-3">
              {parsedResult.prior_art_cited.map((ref: any, idx: number) => (
                <div key={idx} className="bg-white p-3 rounded border border-blue-300">
                  <div className="font-medium mb-2 text-blue-900">{ref.reference}</div>
                  {ref.relevance && (
                    <div className="text-sm mb-1"><span className="font-medium">Relevance:</span> {ref.relevance}</div>
                  )}
                  {ref.key_teachings && (
                    <div className="text-sm"><span className="font-medium">Key Teachings:</span> {ref.key_teachings}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Requirements */}
        {parsedResult.requirements && Array.isArray(parsedResult.requirements) && parsedResult.requirements.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2 text-purple-900">Requirements</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {parsedResult.requirements.map((req: any, idx: number) => (
                <li key={idx}>{typeof req === 'string' ? req : req.description || JSON.stringify(req)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
